import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  BackwardIcon,
  Button,
  H2,
  H3,
  InfoBlock,
  Input,
  ISelectOption,
  Preloader,
  Select,
  Toggle,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { useWindowResize } from 'hooks';
import { useDeleteCombo } from 'hooks/combo';
import { LocalizationContext } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';
import { EComboItemType } from 'types/combo';
import { secondsToWeekDays } from 'utils';

import s from './Combo.module.scss';
import { DEFAULT_VALUES } from './constants';
import { EntityField } from './EntityField';
import { TEntity, TFormValues, TNormalizedEntity, TTariff } from './interfaces';
import { Preview } from './Preview';
import {
  ADDITIONAL_DATA,
  COMBO,
  CREATE_COMBO,
  TAdditionalDataResponse,
  TComboRequest,
  TComboResponse,
  TCreateComboRequest,
  TCreateComboResponse,
  TUpdateComboRequest,
  TUpdateComboResponse,
  UPDATE_COMBO,
} from './schema';
import { TariffField } from './TariffField';
import { filterByTax } from './utils';

export const Combo: FC = () => {
  const { t } = useContext(LocalizationContext);

  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const paramID = params.id === 'new' ? null : Number(params.id);
  const [countValid, setCountValid] = useState(true);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isDirty, isSubmitting },
  } = useForm<TFormValues>({
    mode: 'onChange',
    defaultValues: { ...DEFAULT_VALUES },
  });

  const deleteCombo = useDeleteCombo();
  const { data: loadedCombo, loading: isComboLoading } = useQuery<
    TComboResponse,
    TComboRequest
  >(COMBO, {
    fetchPolicy: 'cache-and-network',
    skip: !paramID,
    variables: {
      id: paramID ?? 0,
    },
    onCompleted: ({ combo }) => {
      const servicesTmp: Array<TEntity> = [];
      const goodsTmp: Array<TEntity> = [];

      for (const item of combo.items) {
        let tmp: Array<TEntity> = [];

        switch (item.entity_type) {
          case EComboItemType.GOOD:
            tmp = goodsTmp;
            break;
          case EComboItemType.SERVICE:
            tmp = servicesTmp;
            break;
        }

        tmp.push({
          entity_id: item.entity.id,
          entity_type: item.entity_type,
          price: item.price,
          amount: item.amount,
        });
      }

      reset({
        tariff: combo.tariff?.id ?? null,
        title: combo.title,
        group: combo.host_group?.id ?? null,
        use_global_discount: combo.use_global_discount,
        services: servicesTmp,
        goods: goodsTmp,
      });
    },
  });
  const [createCombo] = useMutation<TCreateComboResponse, TCreateComboRequest>(
    CREATE_COMBO,
    {
      onCompleted: () => {
        navigate('/combos', { replace: true });
      },
      onError: (error) => {
        if (error.message === 'combo items count less than 2') {
          setCountValid(false);
        }
      },
    }
  );
  const [updateCombo] = useMutation<TUpdateComboResponse, TUpdateComboRequest>(
    UPDATE_COMBO,
    {
      onCompleted: () => {
        navigate('/combos', { replace: true });
      },
      onError: (error) => {
        if (error.message === 'combo items count less than 2') {
          setCountValid(false);
        }
      },
    }
  );
  const { data: additionalDataResponse, loading } =
    useQuery<TAdditionalDataResponse>(ADDITIONAL_DATA, {
      fetchPolicy: 'cache-and-network',
    });

  const { GROUPS_OPTIONS } = useMemo(
    () => ({
      GROUPS_OPTIONS:
        additionalDataResponse?.hostGroupsOverview.map<ISelectOption>(
          (group) => ({
            value: group.id,
            title: group.title,
          })
        ) ?? [],
    }),
    [additionalDataResponse]
  );
  const { NORMALIZED_GOODS, NORMALIZED_SERVICES } = useMemo(() => {
    return {
      NORMALIZED_GOODS:
        additionalDataResponse?.goods.reduce<Record<number, TNormalizedEntity>>(
          (acc, entity) => {
            acc[entity.id] = entity;

            return acc;
          },
          {}
        ) ?? [],
      NORMALIZED_SERVICES:
        additionalDataResponse?.services.reduce<
          Record<number, TNormalizedEntity>
        >((acc, entity) => {
          acc[entity.id] = entity;

          return acc;
        }, {}) ?? [],
    };
  }, [additionalDataResponse]);

  const title = watch('title');
  const groupId = watch('group');
  const tariff = watch('tariff');
  const goods = watch('goods');
  const services = watch('services');
  const useDiscounts = watch('use_global_discount');

  const selectedGroupTitle = GROUPS_OPTIONS.find(
    (group) => group.value === groupId
  )?.title;

  const availableTariffs = useMemo(() => {
    const tmp = [];

    for (const tariff of additionalDataResponse?.tariffs.data ?? []) {
      if (tariff.per_minute || tariff.pausable) continue;

      const tmpTariff: TTariff = {
        id: tariff.id,
        title: tariff.title,
        price: -1,
        isPackage: tariff.has_fixed_finish_time,
        duration: tariff.has_fixed_finish_time
          ? secondsToWeekDays(tariff.schedules[0].end).hour
          : tariff.duration.toString(),
      };

      for (const priceListElement of tariff.price_list) {
        if (
          !priceListElement.host_groups.some((group) => group.id === groupId)
        ) {
          continue;
        }

        tmpTariff.price =
          priceListElement.cost_map.find((cost) => cost.title === 'DEFAULT')
            ?.value ?? -1;
      }

      if (tmpTariff.price === -1) continue;

      tmp.push(tmpTariff);
    }

    return tmp;
  }, [additionalDataResponse, groupId]);

  const selectedTariff = useMemo(
    () => availableTariffs.find((item) => item.id === tariff),
    [availableTariffs, tariff]
  );

  useEffect(() => {
    if (loadedCombo?.combo.host_group?.id === groupId) return;

    setValue('tariff', null);
  }, [groupId]);

  const selectedGoods = goods.map((good) => ({
    ...good,
    basePrice: NORMALIZED_GOODS[good.entity_id]?.cost,
    title: NORMALIZED_GOODS[good.entity_id]?.title,
  }));
  const selectedServices = services.map((service) => ({
    ...service,
    basePrice: NORMALIZED_SERVICES[service.entity_id]?.cost,
    title: NORMALIZED_SERVICES[service.entity_id]?.title,
  }));

  const tax = useMemo(() => {
    for (const good of goods) {
      const tmp = NORMALIZED_GOODS[good.entity_id]?.tax_system;
      if (tmp) return tmp;
    }

    for (const service of services) {
      const tmp = NORMALIZED_SERVICES[service.entity_id]?.tax_system;
      if (tmp) return tmp;
    }
  }, [services, goods, NORMALIZED_GOODS, NORMALIZED_SERVICES]);

  const counts = useMemo(() => {
    return goods.length + services.length + (tariff === null ? 0 : 1);
  }, [goods, services, tariff]);

  const disableTariff = useMemo(() => {
    if (counts === 5 && tariff === null) return true;

    return !groupId;
  }, [counts, tariff, groupId]);

  useEffect(() => {
    if (!goods?.length && !services?.length && !tariff) return;

    if (counts < 2) return setCountValid(false);

    setCountValid(true);
  }, [counts, tariff, goods, services]);

  const size = useWindowResize();
  return (
    <div className={s.container}>
      <div className={s.link} onClick={() => navigate('/combos')}>
        <BackwardIcon />
        {t('back')}
      </div>
      <Preloader loading={loading || isComboLoading}>
        <header className={s.header}>
          {t(paramID ? 'combo.editing' : 'combo.creating')}
        </header>
        <div className={s.data}>
          <form
            onSubmit={handleSubmit((data) => {
              const input = {
                title: data.title,
                host_group_id: data.group,
                tariff_id: data.tariff,
                use_global_discount: data.use_global_discount,
                items: [...data.goods, ...data.services].map((entity) => ({
                  amount: entity.amount,
                  price: entity.price,
                  entity_id: entity.entity_id,
                  entity_type: entity.entity_type,
                })),
              };

              return paramID
                ? updateCombo({ variables: { id: paramID, input } })
                : createCombo({
                    variables: { input },
                  });
            })}
            className={s.form}
          >
            <div className={s.form_block}>
              <div className={s.left}>
                <div className={s.block}>
                  <H2 className={s.title}>{t('goods.edit.subtitle_basic')}</H2>

                  <div className={s.fields}>
                    <div className={s.field}>
                      <H3>{t('title')}</H3>
                      <Controller
                        render={({ field, fieldState: { error } }) => (
                          <Input
                            {...field}
                            variant="secondary"
                            placeholder={t('title.insert')}
                            maxLength={50}
                            error={error?.message}
                            bordered
                          />
                        )}
                        name="title"
                        control={control}
                        rules={{
                          required: t('errors.required'),
                          minLength: {
                            value: 2,
                            message: t('errors.minLength', 2),
                          },
                          maxLength: {
                            value: 50,
                            message: t('errors.maxLength', 50),
                          },
                        }}
                      />
                    </div>
                    <div className={s.field}>
                      <H3>{t('combo.group')}</H3>

                      <Controller
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={GROUPS_OPTIONS}
                            placeholder={t('tariffs.filters.group')}
                            bordered
                          />
                        )}
                        name="group"
                        control={control}
                      />
                    </div>
                    <div className={s.field}>
                      <H3>{t('tariff')}</H3>
                      <div className={s.fields}>
                        <Controller
                          render={({ field }) => (
                            <TariffField
                              value={field.value}
                              onChange={field.onChange}
                              tariffs={availableTariffs}
                              disabled={disableTariff}
                            />
                          )}
                          name="tariff"
                          control={control}
                        />
                      </div>
                    </div>
                    <div className={s.field}>
                      <H3>{t('good')}</H3>
                      <EntityField
                        type={EComboItemType.GOOD}
                        control={control}
                        options={filterByTax(
                          additionalDataResponse?.goods,
                          tax
                        )}
                        normalizedEntities={NORMALIZED_GOODS}
                        disabled={counts === 5}
                      />
                    </div>

                    <div className={s.field}>
                      <H3>{t('service')}</H3>
                      <EntityField
                        type={EComboItemType.SERVICE}
                        control={control}
                        options={filterByTax(
                          additionalDataResponse?.services,
                          tax
                        )}
                        normalizedEntities={NORMALIZED_SERVICES}
                        disabled={counts === 5}
                      />
                    </div>
                  </div>
                </div>

                <div className={s.block}>
                  <H2>{t('additional')}</H2>
                  <Controller
                    render={({ field }) => (
                      <Toggle
                        value={field.value}
                        onChange={field.onChange}
                        label={t('discount.club.apply')}
                        variant="secondary"
                      />
                    )}
                    name="use_global_discount"
                    control={control}
                  />
                </div>

                {!countValid && (
                  <InfoBlock
                    className={s.info_block}
                    message={t('combo.goods.error')}
                  />
                )}
              </div>
              <div className={s.preview}>
                {size.width < 960 && <H2>{t('combo.preview.title')}</H2>}
                <Preview
                  title={title}
                  group={selectedGroupTitle}
                  tariff={selectedTariff}
                  goods={selectedGoods}
                  services={selectedServices}
                  useDiscounts={useDiscounts}
                />
              </div>
            </div>
            <div className={s.controls}>
              {paramID && (
                <Button
                  variant="secondary"
                  color="red"
                  className={s.remove}
                  onClick={() => {
                    openConfirmDialog(t, 'remove', {
                      title: t('combo.deleting'),
                      message: t('combo.deleting.confirm', title),
                    }).then(({ status }) => {
                      if (status === 'ACCEPTED') {
                        return deleteCombo({ variables: { id: paramID } }).then(
                          () => navigate('/combos', { replace: true })
                        );
                      }
                    });
                  }}
                >
                  {t('delete')}
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                className={!paramID ? s.button_create : ''}
                disabled={isSubmitting || !isDirty || !countValid}
              >
                {paramID ? t('save') : t('create')}
              </Button>
            </div>
          </form>
        </div>
      </Preloader>
    </div>
  );
};
